import {
  BRAND_API_MESSAGES,
  BRAND_FIELD_DEFINITIONS,
  WEBSITE_URL_ERROR_TEXT,
  type BrandListFieldKey,
  type BrandOnboardingStatus,
  type BrandSectionKey,
} from '@mg/shared';
import { CHANGES_SAVED } from '../../components/ui/toast';
import { REREAD_ENABLED } from './flags/reread';
import { SUGGESTIONS_ENABLED } from './flags/suggestions';
import { URL_SCAN_ENABLED } from './flags/urlScan';

/**
 * The Brand Workspace copy deck (web.md §12; spec quotes exact). Release 3 text first. A string that
 * Release 4 changes takes its Release 4 wording once that lane flips its flag file (spec:64, :66, :87,
 * :88): web pages with URL_SCAN_ENABLED, suggestions with SUGGESTIONS_ENABLED, re-reading with
 * REREAD_ENABLED. No string here says "this brand" or "this company" (spec:28; wording.gate.test.ts).
 */

/** The Release 3 text, or the Release 4 text once its feature is on. */
const byFlag = (enabled: boolean, release3: string, release4: string): string => (enabled ? release4 : release3);

// ── The wizard ──────────────────────────────────────────────────────────────────

export const WIZARD_COPY = {
  heading: { setup: 'Set up your brand', revisit: 'Revisit your brand setup' },
  sentence: {
    setup: byFlag(
      URL_SCAN_ENABLED,
      "Upload your brand guidelines and we'll prefill these steps, or enter your brand's details yourself.",
      "Upload your brand guidelines or add web pages and we'll prefill these steps, or enter your brand's details yourself.",
    ),
    revisit: byFlag(
      REREAD_ENABLED,
      "Review your brand's details step by step and save any step you change.",
      "Have AI re-read your sources, or review and update your brand's details yourself.",
    ),
  },
  /** Step 1's title (web.md §4.2). */
  step1Title: { setup: 'How would you like to start?', revisit: 'How would you like to update your brand details?' },
  step1Description: {
    setup: "Choose how to fill in your brand's details. You can change anything before you save each step.",
    revisit: "Choose how to update your brand's details.",
  },
} as const;

/** Step 1's two radio cards in setup mode (spec:87-88). */
export const START_CHOICE_COPY = {
  upload: {
    label: 'Upload and prefill',
    description: byFlag(
      URL_SCAN_ENABLED,
      'Add your brand document. We read it and fill in the next steps for you to review.',
      'Add your brand document and web pages. We read them and fill in the next steps for you to review.',
    ),
  },
  manual: {
    label: 'Fill in manually',
    description: byFlag(
      SUGGESTIONS_ENABLED,
      "Enter your brand's details yourself, step by step.",
      "Enter your brand's details yourself, step by step. We suggest answers where we can.",
    ),
  },
} as const;

/** Shown under the cards when Fill in manually is chosen, once suggestions exist (spec:88). Release 4 only. */
export const MANUAL_SUGGESTIONS_NOTICE =
  'Where a field is empty, we\'ll suggest an answer based on your brand\'s name, website and what you\'ve entered so far. Suggestions are always marked "AI suggested".';

/** Revisit's one choice in Release 3 (spec:129). */
export const EDIT_MANUALLY_CHOICE = {
  label: 'Edit manually',
  description: 'Nothing changes until you edit a field and save the step.',
} as const;

/** Revisit's second choice, Release 4 only (spec:129). */
export const REREAD_CHOICE = {
  label: 'Re-read my sources',
  description: 'AI re-reads your document and web pages and proposes updates.',
} as const;

// ── Sections: the wizard's step cards and the Edit page's cards ───────────────

export interface SectionCopy {
  title: string;
  description: string;
}

/** Each section's title and one-line description, shared by the wizard's step cards and the Edit cards. */
export const SECTION_COPY: Readonly<Record<BrandSectionKey, SectionCopy>> = {
  workspaceDetails: {
    title: 'Brand Workspace Details',
    description: "Your brand's name in Smorgas Social and its public website.",
  },
  companyVoice: {
    title: 'Company voice and identity',
    description: 'What your company values, the problem it is solving and where it is going.',
  },
  brandIdentity: {
    title: 'Brand identity',
    description: "The problems your brand's customers face and how your brand solves them.",
  },
  brandVoice: {
    title: 'Brand voice',
    description: 'How your brand sounds and what it promises, whoever it is talking to.',
  },
  vocabulary: {
    title: 'Vocabulary',
    description: "Words and phrases the AI uses, avoids and adds to your brand's social posts.",
  },
  writingGuidelines: {
    title: 'Writing guidelines',
    description: 'Rules the AI follows in everything it writes for your brand.',
  },
  audiences: {
    title: 'Audiences',
    description: 'Who your brand talks to, and how it speaks to each of them.',
  },
  market: {
    title: 'Market',
    description: 'Where your brand operates, what sets it apart and how it describes its value.',
  },
};

/** The "Add…" box of each Vocabulary list. */
export const TAG_PLACEHOLDERS: Readonly<Record<Exclude<BrandListFieldKey, 'writingGuidelines'>, string>> = {
  keywords: 'Add a keyword',
  alwaysSay: 'Add a word or phrase to use',
  neverSay: 'Add a word or phrase to avoid',
  socialHashtags: 'Add a hashtag',
  catchphrases: 'Add a catchphrase or slogan',
};

/** The Writing guidelines table's add box, with the spec's example rule (spec:265). */
export const GUIDELINE_PLACEHOLDER = `e.g. ${BRAND_FIELD_DEFINITIONS.writingGuidelines.placeholder ?? ''}`;

// ── The Edit page ───────────────────────────────────────────────────────────────

/** The onboarding button and the notice text beside it in the Brand information card (spec:62-66). */
export const ONBOARDING_NOTICE: Readonly<Record<BrandOnboardingStatus, { button: string; text: string }>> = {
  NOT_STARTED: {
    button: 'Start Brand Setup',
    text: byFlag(
      URL_SCAN_ENABLED,
      "You'll be able to upload your brand's written guidelines document. We'll use it to prefill the fields below.",
      "You'll be able to upload your brand's written guidelines document and add URLs to scan for your brand's information. We'll use them to prefill the fields below.",
    ),
  },
  IN_PROGRESS: {
    button: 'Continue Brand Setup',
    text: "You've started Brand Setup but haven't finished. Continue where you left off to complete your brand's details.",
  },
  FINISHED: {
    button: 'Revisit Brand Setup',
    text: byFlag(
      REREAD_ENABLED,
      "Brand Setup is complete. Revisit it to review or update your brand's details.",
      "Brand Setup is complete. Revisit it to review or update your brand's details. AI can re-read your sources and show what changed.",
    ),
  },
};

/** The confirmation before a finished Brand Setup is revisited (spec:66). */
export const REVISIT_CONFIRM = {
  title: 'Revisit Brand Setup?',
  message: byFlag(
    REREAD_ENABLED,
    "Revisiting Brand Setup can replace your brand's current settings.",
    "Revisiting Brand Setup can replace your brand's current settings. Changes are highlighted so you can review them before they are saved.",
  ),
  confirmLabel: 'Revisit Brand Setup',
  cancelLabel: 'Cancel',
} as const;

/** The Unsaved changes dialog's body on the Edit pages (spec:198); R1's dialog draws the title and buttons. */
export const EDIT_UNSAVED_MESSAGE =
  "This page has changes to your brand settings that haven't been saved. Save them before leaving, or discard them.";

/** What a member sees at the top of the Edit pages (web.md §6.1; owner question 10). */
export const MEMBER_READ_ONLY_NOTICE =
  'You have read-only access to these settings. Ask a Brand Workspace Admin to make changes.';

/** What a member sees instead of the wizard (web.md §4.1; owner question 10). */
export const MEMBERS_WIZARD_REFUSAL = {
  title: 'Brand Setup is for Brand Workspace Admins',
  message: "Ask a Brand Workspace Admin to set up or update your brand's details.",
  action: 'Back to Edit Brand Workspace',
} as const;

// ── Shared ──────────────────────────────────────────────────────────────────────

/** The toasts on these pages (spec:143-144, :289). */
export const BRAND_TOASTS = {
  changesSaved: CHANGES_SAVED,
  stepSaved: 'Step saved',
  setupCompleted: 'Brand Setup completed',
} as const;

/** A save refused because the brand changed in another tab or by another admin (409), with its button. */
export const VERSION_CONFLICT_COPY = {
  message: BRAND_API_MESSAGES.VERSION_CONFLICT,
  action: 'Reload',
} as const;

/** The website box's error for an address that is not a public web address (the API says the same). */
export const WEBSITE_INVALID_TEXT = WEBSITE_URL_ERROR_TEXT.INVALID;
